import type { DpdEntry } from './performance.ts'

/**
 * The days-past-due bucket schemes the conversion chart offers.
 *
 * Three ways of grouping the same distribution, because three audiences read it
 * differently and none of them is wrong. The endpoint serves one entry per
 * distinct day — 31 of them across all time — so switching scheme is arithmetic
 * in the browser rather than another query.
 *
 * A scheme is a list of lower bounds. The band runs from its own bound to one
 * below the next, and the last band is open at the top, so the bands are
 * contiguous by construction rather than by a reviewer checking the numbers.
 * That is what a test here holds: a day in two bands is counted twice, and a
 * day in none vanishes from the chart whose subject is where the days land.
 */

/** One band, and the cycles that fell in it. */
export interface DpdBucket {
  label: string
  from: number
  /** Null on the last band, which is open at the top. */
  to: number | null
  cycles: number
  paid: number
  /** Null when no cycle fell in the band — never called is not 0% paid. */
  paidRate: number | null
}

export type DpdSchemeId = 'standard' | 'sales' | 'fine'

export interface DpdScheme {
  id: DpdSchemeId
  label: string
  /** The lower bound of each band, ascending. The first is always 0. */
  bounds: readonly number[]
}

/**
 * Every third day to 30, then open.
 *
 * Generated rather than typed out: eleven hand-written bounds is eleven chances
 * to fat-finger one, and the gap would show as a band silently missing from the
 * chart.
 */
const fineBounds = [
  0,
  ...Array.from({ length: 10 }, (_, step) => step * 3 + 1),
  31
]

export const DPD_SCHEMES: readonly DpdScheme[] = Object.freeze([
  {
    id: 'standard',
    label: 'Standard (0 / 1-7 / 8-29 / 30+)',
    bounds: Object.freeze([0, 1, 8, 30])
  },
  {
    id: 'sales',
    label: 'Sales (0 / 1-7 / 8-15 / 16-30 / 31+)',
    bounds: Object.freeze([0, 1, 8, 16, 31])
  },
  {
    id: 'fine',
    label: 'Every 3 days (0 / 1-3 / 4-6 / … / 31+)',
    bounds: Object.freeze(fineBounds)
  }
])

/**
 * Read a scheme back off a control.
 *
 * An unknown id answers null rather than the first scheme, so a stale link
 * cannot quietly draw a different grouping than the one it names.
 *
 * @param value - A candidate scheme id.
 * @returns The scheme, or null when the value names none.
 */
export function parseDpdScheme(value: unknown): DpdScheme | null {
  return DPD_SCHEMES.find(scheme => scheme.id === value) ?? null
}

/**
 * How a band between two bounds is named.
 *
 * `0` alone rather than `0-0`, and the open band as `31+`.
 *
 * @param from - The band's lower bound.
 * @param to - Its upper bound, or null when the band is open.
 * @returns The label the chart prints.
 */
function bandLabel(from: number, to: number | null): string {
  if (to === null) {
    return `${from}+`
  }

  return from === to ? String(from) : `${from}-${to}`
}

/**
 * Group a days-past-due distribution into one scheme's bands.
 *
 * A band nobody was called in keeps its place with a null rate rather than
 * being dropped here. The chart decides whether to draw it; losing it in the
 * arithmetic would make "we never called this band" indistinguishable from
 * "this band is not part of the scheme".
 *
 * @param histogram - One entry per distinct days-past-due value.
 * @param scheme - The scheme to group by.
 * @returns One bucket per band, in ascending age order.
 */
export function bucketByScheme(
  histogram: readonly DpdEntry[],
  scheme: DpdScheme
): DpdBucket[] {
  const buckets = scheme.bounds.map((from, index) => {
    const next = scheme.bounds[index + 1]
    const to = next === undefined ? null : next - 1

    return { label: bandLabel(from, to), from, to, cycles: 0, paid: 0 }
  })

  for (const entry of histogram) {
    // Walk down to the last band whose lower bound the day reaches. Bands
    // ascend and are contiguous, so exactly one matches every day at or above
    // the first bound. A plain loop rather than `findLast`, which needs a
    // newer lib target than this project sets.
    for (let index = buckets.length - 1; index >= 0; index -= 1) {
      const bucket = buckets[index]
      if (bucket && entry.dpd >= bucket.from) {
        bucket.cycles += entry.cycles
        bucket.paid += entry.paid
        break
      }
    }
  }

  return buckets.map(bucket =>
    Object.assign(bucket, {
      paidRate:
        bucket.cycles === 0
          ? null
          : Math.round((bucket.paid / bucket.cycles) * 10_000) / 10_000
    })
  )
}
